import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtAUD } from "@/lib/api";
import DateRangePicker from "@/components/DateRangePicker";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, LineChart, Line, CartesianGrid } from "recharts";

const TABS = [
  { id: "service", label: "Service Performance" },
  { id: "source", label: "Lead Source" },
  { id: "clients-source", label: "Clients by Lead Source" },
  { id: "team", label: "Team Performance" },
  { id: "forecast", label: "Revenue Forecast" },
  { id: "partner-leads", label: "Leads by Partner" },
  { id: "partner-opps", label: "Opps by Partner" },
  { id: "campaign-leads", label: "Leads by Campaign" },
  { id: "campaign-opps", label: "Opps by Campaign" },
];

export default function Reports() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "service");
  const [data, setData] = useState(null);
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  useEffect(() => {
    const cur = searchParams.get("tab");
    if (cur !== tab) {
      const p = new URLSearchParams(searchParams);
      p.set("tab", tab);
      setSearchParams(p, { replace: true });
    }
  }, [tab]);

  useEffect(() => {
    const ROUTES = {
      service: { url: "/reports/service-performance" },
      source: { url: "/reports/source-performance" },
      "clients-source": { url: "/clients/report/by-source" },
      team: { url: "/reports/team-performance" },
      forecast: { url: "/reports/revenue-forecast" },
      "partner-leads": { url: "/reports/by-partner", params: { entity: "leads" } },
      "partner-opps": { url: "/reports/by-partner", params: { entity: "opportunities" } },
      "campaign-leads": { url: "/reports/by-campaign", params: { entity: "leads" } },
      "campaign-opps": { url: "/reports/by-campaign", params: { entity: "opportunities" } },
    };
    const r = ROUTES[tab];
    if (!r) return;
    setData(null);
    const params = { ...(r.params || {}) };
    if (dateRange.from) params.date_from = dateRange.from;
    if (dateRange.to) params.date_to = dateRange.to;
    api.get(r.url, { params }).then((res) => setData(res.data)).catch(() => setData(null));
  }, [tab, dateRange.from, dateRange.to]);

  return (
    <div className="p-8" data-testid="reports-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="eyb-label">Insights</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Reports</h1>
          <p className="text-sm text-slate-500 mt-1">Pipeline, conversion and revenue analytics across the business.</p>
        </div>
        <DateRangePicker value={dateRange} onChange={setDateRange} defaultPreset="all" />
      </div>

      <div className="flex gap-2 mb-6 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)} className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === t.id ? "border-[#29ABE2] text-[#187199]" : "border-transparent text-slate-500 hover:text-slate-900"}`}>{t.label}</button>
        ))}
      </div>

      {!data ? <div className="text-sm text-slate-400">Loading…</div> :
        tab === "service" ? <ServiceReport data={data} /> :
        tab === "source" ? <SourceReport data={data} /> :
        tab === "clients-source" ? <ClientsBySourceReport data={data} dateRange={dateRange} /> :
        tab === "team" ? <TeamReport data={data} /> :
        tab === "forecast" ? <ForecastReport data={data} /> :
        tab === "partner-leads" ? <PartnerReport data={data} entity="leads" /> :
        tab === "partner-opps" ? <PartnerReport data={data} entity="opportunities" /> :
        tab === "campaign-leads" ? <CampaignReport data={data} entity="leads" /> :
        tab === "campaign-opps" ? <CampaignReport data={data} entity="opportunities" /> :
        null
      }
    </div>
  );
}

function Table({ columns, rows, footer }) {
  return (
    <div className="eyb-card overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50/60 border-b border-slate-200">
            {columns.map((c) => <th key={c.key} className="px-4 py-3 font-semibold">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100">
              {columns.map((c) => <td key={c.key} className="px-4 py-3 text-slate-700">{c.render ? c.render(r[c.key], r) : r[c.key]}</td>)}
            </tr>
          ))}
        </tbody>
        {footer && <tfoot>{footer}</tfoot>}
      </table>
    </div>
  );
}

function ServiceReport({ data }) {
  return (
    <div className="space-y-6">
      <div className="eyb-card p-6">
        <h3 className="font-heading font-semibold text-slate-900 mb-4">Revenue by Service Category</h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="category" tick={{ fontSize: 11, fill: "#64748b" }} angle={-25} textAnchor="end" height={90} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v) => fmtAUD(v)} />
              <Bar dataKey="revenue" fill="#29ABE2" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <Table columns={[
        { key: "category", label: "Category" },
        { key: "leads", label: "Leads" },
        { key: "opportunities", label: "Opps" },
        { key: "closed_won", label: "Won" },
        { key: "closed_lost", label: "Lost" },
        { key: "conversion_rate", label: "Conv %", render: (v) => `${v}%` },
        { key: "revenue", label: "Revenue", render: (v) => fmtAUD(v) },
        { key: "avg_value", label: "Avg Value", render: (v) => fmtAUD(v) },
      ]} rows={data} />
    </div>
  );
}

function SourceReport({ data }) {
  return (
    <div className="space-y-6">
      <Table columns={[
        { key: "source", label: "Source" },
        { key: "leads", label: "Leads" },
        { key: "opportunities", label: "Opps" },
        { key: "closed_won", label: "Won" },
        { key: "closed_lost", label: "Lost" },
        { key: "conversion_rate", label: "Conv %", render: (v) => `${v}%` },
        { key: "revenue", label: "Revenue", render: (v) => fmtAUD(v) },
        { key: "avg_deal_size", label: "Avg Deal", render: (v) => fmtAUD(v) },
      ]} rows={data} />
    </div>
  );
}

function TeamReport({ data }) {
  return (
    <Table columns={[
      { key: "name", label: "Member" },
      { key: "role", label: "Role", render: (v) => v.replace("_", " ") },
      { key: "leads_created", label: "Leads" },
      { key: "opps_created", label: "Opps" },
      { key: "closed_won", label: "Won" },
      { key: "closed_lost", label: "Lost" },
      { key: "conversion_rate", label: "Conv %", render: (v) => `${v}%` },
      { key: "pipeline_value", label: "Pipeline", render: (v) => fmtAUD(v) },
      { key: "won_value", label: "Won Value", render: (v) => fmtAUD(v) },
      { key: "avg_days_to_close", label: "Avg Days" },
    ]} rows={data} />
  );
}

function ForecastReport({ data }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="eyb-card p-5">
          <div className="eyb-label">Total Pipeline</div>
          <div className="font-heading text-3xl font-bold text-slate-900 mt-2">{fmtAUD(data.total_pipeline)}</div>
        </div>
        <div className="eyb-card p-5">
          <div className="eyb-label">Weighted Pipeline</div>
          <div className="font-heading text-3xl font-bold text-slate-900 mt-2">{fmtAUD(data.weighted_pipeline)}</div>
        </div>
      </div>

      <div className="eyb-card p-6">
        <h3 className="font-heading font-semibold text-slate-900 mb-4">Forecast by Month (weighted)</h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.by_month}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="period" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip formatter={(v) => fmtAUD(v)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Line type="monotone" dataKey="value" stroke="#29ABE2" strokeWidth={3} dot={{ fill: "#1B4F8B", r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="eyb-card p-6">
        <h3 className="font-heading font-semibold text-slate-900 mb-4">Forecast by Quarter (weighted)</h3>
        <div className="h-60">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.by_quarter}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="period" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip formatter={(v) => fmtAUD(v)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="value" fill="#1B4F8B" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}


function PartnerReport({ data, entity }) {
  const rows = data?.rows || [];
  const isOpps = entity === "opportunities";
  return (
    <div data-testid="report-by-partner" className="space-y-4">
      <div className="text-xs text-slate-500">{data?.total ?? 0} {isOpps ? "opportunities" : "leads"} grouped by partnership / partner firm</div>
      <Table
        columns={[
          { key: "partnership", label: "Partnership Category" },
          { key: "partner", label: "Partner Firm" },
          { key: "count", label: isOpps ? "Opps" : "Leads" },
          ...(isOpps ? [
            { key: "open_value", label: "Open Pipeline", render: (v) => fmtAUD(v) },
            { key: "won_value", label: "Closed Won", render: (v) => fmtAUD(v) },
            { key: "won_count", label: "Won # / Lost #", render: (_, r) => `${r.won_count} / ${r.lost_count}` },
            { key: "win_rate", label: "Win rate", render: (v) => `${v}%` },
          ] : []),
        ]}
        rows={rows}
      />
      {rows.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No data for this period.</div>}
    </div>
  );
}

function CampaignReport({ data, entity }) {
  const rows = data?.rows || [];
  const isOpps = entity === "opportunities";
  return (
    <div data-testid="report-by-campaign" className="space-y-4">
      <div className="text-xs text-slate-500">{data?.total ?? 0} {isOpps ? "opportunities" : "leads"} grouped by campaign</div>
      <Table
        columns={[
          { key: "campaign_name", label: "Campaign" },
          { key: "campaign_status", label: "Status", render: (v) => v || "—" },
          { key: "count", label: isOpps ? "Opps" : "Leads" },
          ...(isOpps ? [
            { key: "open_value", label: "Open Pipeline", render: (v) => fmtAUD(v) },
            { key: "won_value", label: "Closed Won", render: (v) => fmtAUD(v) },
            { key: "won_count", label: "Won # / Lost #", render: (_, r) => `${r.won_count} / ${r.lost_count}` },
            { key: "win_rate", label: "Win rate", render: (v) => `${v}%` },
          ] : []),
        ]}
        rows={rows}
      />
      {rows.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No data for this period.</div>}
    </div>
  );
}

function ClientsBySourceReport({ data, dateRange }) {
  const rows = (data?.by_source || []).filter((r) => r.clients > 0);
  const totals = data?.totals || { clients: 0, value: 0, avg_value: 0 };
  const exportCsv = async () => {
    const token = localStorage.getItem("eyb_token");
    const params = new URLSearchParams();
    if (dateRange?.from) params.set("date_from", dateRange.from);
    if (dateRange?.to) params.set("date_to", dateRange.to);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/clients/report/export.csv?${params.toString()}`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "clients_report.csv";
    a.click();
  };
  return (
    <div data-testid="report-clients-by-source" className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="eyb-card p-4">
          <div className="eyb-label">Total Clients</div>
          <div className="eyb-kpi-value mt-2">{totals.clients}</div>
        </div>
        <div className="eyb-card p-4">
          <div className="eyb-label">Total Value</div>
          <div className="eyb-kpi-value mt-2" style={{ color: "var(--eyb-emerald)" }}>{fmtAUD(totals.value)}</div>
        </div>
        <div className="eyb-card p-4">
          <div className="eyb-label">Average Deal</div>
          <div className="eyb-kpi-value mt-2">{fmtAUD(totals.avg_value)}</div>
        </div>
      </div>

      {rows.length > 0 && (
        <div className="eyb-card p-4" data-testid="report-clients-chart">
          <div className="eyb-label mb-3">Revenue by Lead Source</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={rows} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DE" vertical={false} />
              <XAxis dataKey="source" tick={{ fontSize: 11, fill: "#5A6B80" }} interval={0} angle={-15} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11, fill: "#5A6B80" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                formatter={(v, key) => key === "value" ? fmtAUD(v) : v}
                labelStyle={{ color: "#0A2540", fontWeight: 600 }}
                contentStyle={{ background: "#fff", border: "1px solid #E7E5DE", borderRadius: 10 }}
              />
              <Bar dataKey="value" name="Revenue" fill="#1B4F8B" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">{totals.clients} clients grouped by lead source</div>
        <button
          data-testid="clients-report-export"
          onClick={exportCsv}
          className="text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-200 bg-white hover:border-slate-300"
        >
          Export CSV
        </button>
      </div>

      <Table
        columns={[
          { key: "source", label: "Lead Source" },
          { key: "clients", label: "Clients" },
          { key: "value", label: "Revenue", render: (v) => fmtAUD(v) },
          { key: "avg_value", label: "Avg Deal", render: (v) => fmtAUD(v) },
          { key: "share", label: "Share", render: (v) => `${v}%` },
        ]}
        rows={totals.clients === 0 ? [] : (data?.by_source || []).map((r) => ({ ...r, id: r.source }))}
      />
      {totals.clients === 0 && <div className="text-sm text-slate-400 py-6 text-center">No clients in this period.</div>}
    </div>
  );
}

